// Gel des règles prop firm au niveau du compte.
//
// POURQUOI. Les règles (drawdown, daily loss, consistency) vivaient uniquement
// dans le référentiel partagé (PropFirmTemplate → AccountType → Tier → Phase) et
// étaient relues à CHAQUE calcul. Changer une valeur du référentiel réécrivait
// donc rétroactivement l'état de tous les comptes qui pointaient dessus — et pas
// seulement à l'affichage : le cycle de sync Tradovate désactive un compte sur
// `bufferStatus.isViolation` et archive en « cramé » / « challenge validé »
// (voir tradovateSyncPipeline.ts et accountClosureReason.ts). Ces écritures-là
// sont irréversibles.
//
// Avec l'arrivée d'un référentiel alimenté par une routine hebdomadaire, ce
// couplage devenait un risque récurrent de destruction d'état. Le compte fige
// donc les règles au moment où l'utilisateur le configure, et plus rien du
// référentiel ne le touche sans son accord explicite.
//
// PAR VALEUR, PAS PAR RÉFÉRENCE. Le snapshot copie les règles au lieu de
// pointer vers un uuid. C'est délibéré : le seed fait un deleteMany sur les
// phases (seedPropFirms.ts) et régénère les uuid à chaque exécution, ce qui peut
// laisser un propFirmTierOverrideId pointer dans le vide. Une copie par valeur
// est immunisée contre ça.

import {
  resolveAccountTypeAndTier,
  type PropFirmTemplateSnapshot,
  type TierSnapshot,
  type AccountTypeSnapshot,
} from "./propFirmEngine";
import { normalizePricingModel } from "./pricingModelVocabulary";

/**
 * Version du format. Un snapshot d'une version inconnue est REJETÉ (retour à
 * null → repli sur le référentiel vivant) plutôt qu'interprété de travers :
 * mal lire des règles gelées produirait des alertes fausses sur des données
 * réelles. Incrémenter à chaque changement non rétrocompatible du format.
 */
export const RULE_SNAPSHOT_VERSION = 1;

export interface RuleSnapshot {
  version: number;
  /** Identité métier de la firme, stable même si le template est reseedé. */
  firmSlug: string;
  firmName: string;
  accountTypeSlug: string;
  accountTypeName: string;
  /** Modèle de tarification figé à l'achat. Optionnel : absent des snapshots
   *  antérieurs au 2026-09-29. `accountPricingModel` (pricingModel.ts) retombe
   *  alors sur le type de compte lié au compte, puis sur "one_time" — jamais
   *  directement sur "one_time" ici, pour ne pas figer un défaut de colonne
   *  encore faux en prod dans un snapshot ancien. TOUJOURS lire le modèle via
   *  `accountPricingModel`, jamais via `resolveRules(...).accountType.pricingModel`,
   *  qui vaut null pour ces snapshots-là. Pas de changement de version : un
   *  champ en plus ne fait pas mal lire les règles. */
  pricingModel?: string;
  /** Les règles elles-mêmes, copiées par valeur (phases incluses). */
  tier: TierSnapshot;
  /** Quand le gel a été posé (ISO). Affiché à l'utilisateur. */
  capturedAt: string;
}

/** D'où viennent les règles effectivement appliquées à un compte. */
export type RuleSource = "snapshot" | "live" | "none";

export interface ResolvedRules {
  accountType: AccountTypeSnapshot | null;
  tier: TierSnapshot | null;
  source: RuleSource;
}

/**
 * Fige les règles applicables à un compte.
 *
 * Appelé aux moments de CONFIGURATION (création du compte, changement de prop
 * firm / plan / taille, acceptation d'une mise à jour), jamais pendant un
 * import : un import ne doit modifier aucun compte utilisateur.
 *
 * Retourne null quand aucun tier ne peut être résolu (compte sans prop firm,
 * template sans palier) — l'absence de snapshot est un état normal.
 */
export function buildRuleSnapshot(
  template: PropFirmTemplateSnapshot | null,
  initialBalance: number,
  accountTypeId: string | null | undefined,
  tierOverrideId: string | null | undefined,
  now: Date = new Date(),
): RuleSnapshot | null {
  if (!template) return null;

  const { accountType, tier } = resolveAccountTypeAndTier(
    template,
    initialBalance,
    accountTypeId ?? null,
    tierOverrideId ?? null,
  );
  if (!accountType || !tier) return null;

  return {
    version: RULE_SNAPSHOT_VERSION,
    firmSlug: template.slug,
    firmName: template.name,
    accountTypeSlug: accountType.slug,
    accountTypeName: accountType.name,
    pricingModel: normalizePricingModel(accountType.pricingModel),
    // Copie profonde : le snapshot ne doit jamais partager de référence avec le
    // référentiel vivant, sinon une mutation en amont le corromprait.
    tier: deepClone(tier),
    capturedAt: now.toISOString(),
  };
}

/**
 * Relit un snapshot stocké en base (colonne Json, donc `unknown` au sortir de
 * Prisma). Tout ce qui n'est pas un snapshot lisible de la version courante
 * retourne null, ce qui fait retomber l'appelant sur le référentiel vivant.
 */
export function readRuleSnapshot(raw: unknown): RuleSnapshot | null {
  if (raw === null || raw === undefined) return null;
  if (typeof raw !== "object" || Array.isArray(raw)) return null;

  const candidate = raw as Partial<RuleSnapshot>;

  if (candidate.version !== RULE_SNAPSHOT_VERSION) return null;
  if (!candidate.tier || typeof candidate.tier !== "object") return null;
  if (typeof candidate.firmSlug !== "string") return null;

  return candidate as RuleSnapshot;
}

/**
 * Point d'entrée unique du moteur : quelles règles s'appliquent à ce compte ?
 *
 * Le snapshot gagne toujours quand il est lisible. Le repli sur le référentiel
 * vivant couvre les comptes pas encore gelés et les snapshots corrompus — mieux
 * vaut des règles vivantes que pas de règles du tout.
 */
export function resolveRules(
  template: PropFirmTemplateSnapshot | null,
  rawSnapshot: unknown,
  initialBalance: number,
  accountTypeId: string | null | undefined,
  tierOverrideId: string | null | undefined,
): ResolvedRules {
  const snapshot = readRuleSnapshot(rawSnapshot);

  if (snapshot) {
    // Le snapshot porte ses propres règles : il reste valable même si le
    // template a disparu du référentiel depuis (firme retirée, reseed).
    const accountType: AccountTypeSnapshot = {
      id: "",
      slug: snapshot.accountTypeSlug,
      name: snapshot.accountTypeName,
      sortOrder: 0,
      isActive: true,
      pricingModel: snapshot.pricingModel ?? null,
      tiers: [snapshot.tier],
    };
    return { accountType, tier: snapshot.tier, source: "snapshot" };
  }

  if (!template) return { accountType: null, tier: null, source: "none" };

  const { accountType, tier } = resolveAccountTypeAndTier(
    template,
    initialBalance,
    accountTypeId ?? null,
    tierOverrideId ?? null,
  );
  if (!tier) return { accountType, tier: null, source: "none" };

  return { accountType, tier, source: "live" };
}

// ─── Diff ────────────────────────────────────────────────────────────────────

export interface RuleChange {
  scope: "tier" | "phase";
  /** Nom de la phase concernée (scope "phase" uniquement). */
  phaseName?: string;
  /** Champ modifié, ou le marqueur __added__ / __removed__ pour une phase. */
  field: string;
  before: unknown;
  after: unknown;
}

/**
 * Champs d'identité technique et d'horodatage, exclus du diff : ils changent à
 * chaque reseed sans qu'aucune RÈGLE n'ait bougé. Les inclure noierait les vrais
 * changements et ferait sonner l'alerte à chaque import.
 *
 * `commissionPerContract` et `symbolCommissions` y sont aussi : les commissions
 * sont un TARIF de pré-remplissage, pas une règle — elles ne doivent ni
 * déclencher la sollicitation « règles modifiées », ni apparaître dans le diff
 * admin. Et `symbolCommissions`, étant un objet, ferait pire que du bruit :
 * `sameValue` (===) le déclarerait différent à CHAQUE comparaison, même entre
 * deux tables identiques `{NQ: 3.5}` (deux références distinctes) — la
 * bannière « vos règles ont changé » resterait affichée en permanence sur tout
 * compte figé sur un tel palier, et « accepter » ne pourrait jamais l'effacer.
 */
// `price` / `priceOriginal` / `resetFee` : des PRIX, pas des regles. Une promo
// ou une hausse de tarif ne doit pas faire sonner « les regles de votre compte
// ont change » cote utilisateur, et un abonnement existant garde de toute
// facon son propre montant fige dans son AccountCharge.
const IGNORED_FIELDS = new Set([
  "id",
  "phases",
  "sortOrder",
  "commissionPerContract",
  "symbolCommissions",
  "price",
  "priceOriginal",
  "resetFee",
]);

/**
 * Compare deux jeux de règles et retourne ce qui a réellement changé.
 *
 * Sert la sollicitation UTILISATEUR (« les règles de votre compte ont
 * changé »), pas l'écran de diff admin avant validation d'un import : celui-ci
 * passe par `computeReferentialDiff` (`admin/propfirm-import/referentialDiff.ts`),
 * qui liste `price` / `priceOriginal` explicitement -- un changement de prix
 * doit rester visible à l'admin qui valide un import, même s'il n'a pas à
 * alerter l'utilisateur final (voir `IGNORED_FIELDS` ci-dessous).
 */
export function diffRuleSnapshots(before: RuleSnapshot, after: RuleSnapshot): RuleChange[] {
  const changes: RuleChange[] = [];

  // Champs plats du tier
  for (const field of unionKeys(before.tier, after.tier)) {
    if (IGNORED_FIELDS.has(field)) continue;
    const b = (before.tier as unknown as Record<string, unknown>)[field] ?? null;
    const a = (after.tier as unknown as Record<string, unknown>)[field] ?? null;
    if (!sameValue(b, a)) changes.push({ scope: "tier", field, before: b, after: a });
  }

  // Phases, appariées par NOM et non par uuid : les uuid sont régénérés à chaque
  // reseed, le nom est ce que l'utilisateur reconnaît.
  const beforePhases = new Map((before.tier.phases ?? []).map((p) => [p.name, p]));
  const afterPhases = new Map((after.tier.phases ?? []).map((p) => [p.name, p]));

  for (const [name, afterPhase] of afterPhases) {
    const beforePhase = beforePhases.get(name);
    if (!beforePhase) {
      changes.push({ scope: "phase", phaseName: name, field: "__added__", before: null, after: afterPhase.phaseType });
      continue;
    }
    for (const field of unionKeys(beforePhase, afterPhase)) {
      if (IGNORED_FIELDS.has(field)) continue;
      const b = (beforePhase as unknown as Record<string, unknown>)[field] ?? null;
      const a = (afterPhase as unknown as Record<string, unknown>)[field] ?? null;
      if (!sameValue(b, a)) changes.push({ scope: "phase", phaseName: name, field, before: b, after: a });
    }
  }

  for (const [name, beforePhase] of beforePhases) {
    if (!afterPhases.has(name)) {
      changes.push({ scope: "phase", phaseName: name, field: "__removed__", before: beforePhase.phaseType, after: null });
    }
  }

  return changes;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function unionKeys(a: object, b: object): string[] {
  return [...new Set([...Object.keys(a), ...Object.keys(b)])];
}

/** null et undefined sont le même « pas de règle ». */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === null && b === null) return true;
  return a === b;
}

function deepClone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
